/* =====================================================================
   VELURA — motor de animación
   Se activa con atributos data-vl-* en el HTML de las secciones:
     data-vl-split[="load"]   titular palabra a palabra (desenfoque → nítido)
     data-vl-reveal           entrada suave al aparecer (data-vl-stagger en el padre)
     data-vl-mask             imagen revelada con máscara
     data-vl-count="60"       contador (data-vl-pad, data-vl-suffix, data-vl-prefix)
     data-vl-scrub            texto que se ilumina con el scroll
     data-vl-parallax="-12"   desplazamiento con el scroll (en %)
     data-vl-bg="#hex"        color de fondo de la página mientras se ve la sección
     data-vl-magnet           botón magnético (ratón)
     data-vl-intro            entra en la coreografía inicial
     data-vl-module="nombre"  componente con lógica propia (Velura.modules)
   ===================================================================== */
(function () {
  'use strict';
  const H = document.documentElement;
  const cfg = window.VL_CFG || {};
  const anim = H.classList.contains('vl-anim');
  const fino = matchMedia('(hover:hover) and (pointer:fine)').matches;
  const editor = !!(window.Shopify && window.Shopify.designMode);
  const G = window.gsap, ST = window.ScrollTrigger;
  if (G && ST) G.registerPlugin(ST);
  window.VL_OK = true;

  const Velura = (window.Velura = window.Velura || {});
  Velura.modules = Velura.modules || {};
  Velura.anim = anim;
  Velura.fino = fino;
  Velura.lenis = null;

  /* ---------- scroll suave (solo ratón, fuera del editor) ---------- */
  function initLenis() {
    if (!anim || !fino || cfg.smooth === false || editor || !window.Lenis || !G) return;
    const lenis = new window.Lenis({ lerp: 0.09 });
    Velura.lenis = lenis;
    if (ST) lenis.on('scroll', ST.update);
    G.ticker.add((t) => lenis.raf(t * 1000));
    G.ticker.lagSmoothing(0);
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href*="#"]');
      if (!a) return;
      const u = new URL(a.href, location.href);
      if (u.pathname !== location.pathname || u.hash.length < 2) return;
      const dest = document.querySelector(u.hash);
      if (!dest) return;
      e.preventDefault();
      lenis.scrollTo(dest, { offset: -90 });
    });
    // el carrito lateral y el menú bloquean el scroll suave mientras están abiertos
    new MutationObserver(() => {
      const bloqueo = document.body.classList.contains('overflow-hidden') || H.classList.contains('vl-menu-abierto');
      bloqueo ? lenis.stop() : lenis.start();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- utilidades ---------- */
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  function inView(els, cb, opts) {
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(cb); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { io.unobserve(en.target); cb(en.target); } });
    }, Object.assign({ rootMargin: '0px 0px -10% 0px', threshold: 0.12 }, opts));
    els.forEach((el) => io.observe(el));
  }

  function split(el) {
    if (el.classList.contains('is-split')) return $$('.vl-w', el);
    const walk = (n) => {
      Array.from(n.childNodes).forEach((c) => {
        if (c.nodeType === 3) {
          const frag = document.createDocumentFragment();
          c.textContent.split(/(\s+)/).forEach((t) => {
            if (!t) return;
            if (/^\s+$/.test(t)) frag.append(t);
            else { const s = document.createElement('span'); s.className = 'vl-w'; s.textContent = t; frag.append(s); }
          });
          c.replaceWith(frag);
        } else if (c.nodeType === 1 && c.tagName !== 'BR') {
          if (c.tagName === 'EM' || c.tagName === 'I') {
            // la cursiva con degradado se reparte por palabras para no romper el recorte del texto
            const palabras = c.textContent.trim().split(/\s+/);
            const frag = document.createDocumentFragment();
            palabras.forEach((p, i) => {
              const em = document.createElement(c.tagName); em.className = (c.className + ' vl-w').trim(); em.textContent = p; frag.append(em);
              if (i < palabras.length - 1) frag.append(' ');
            });
            c.replaceWith(frag);
          } else walk(c);
        }
      });
    };
    walk(el);
    el.classList.add('is-split');
    return $$('.vl-w', el);
  }
  Velura.split = split;

  function contar(el, dur) {
    const to = parseFloat(el.dataset.vlCount) || 0;
    const dec = parseInt(el.dataset.vlDecimals || '0', 10);
    const pad = parseInt(el.dataset.vlPad || '0', 10);
    const pre = el.dataset.vlPrefix || '', suf = el.dataset.vlSuffix || '';
    const fmt = (v) => pre + (dec ? v.toFixed(dec) : String(Math.round(v)).padStart(pad, '0')) + suf;
    if (!anim || !G) { el.textContent = fmt(to); return; }
    const o = { v: parseFloat(el.dataset.vlFrom || '0') };
    G.to(o, { v: to, duration: dur || 1.4, ease: 'power2.out', onUpdate: () => (el.textContent = fmt(o.v)) });
  }
  Velura.contar = contar;

  /* ---------- inicialización por raíz (página o sección recargada en el editor) ---------- */
  function init(root) {
    root = root || document;

    // escalonado automático
    $$('[data-vl-stagger]', root).forEach((p) => {
      const paso = parseFloat(p.dataset.vlStagger) || 0.08;
      $$('[data-vl-reveal],[data-vl-mask]', p).forEach((c, i) => c.style.setProperty('--vl-d', (i * paso).toFixed(2) + 's'));
    });

    // titulares palabra a palabra: se parten justo antes de aparecer (menos trabajo al cargar)
    $$('[data-vl-split]', root).forEach((el) => {
      if (!anim || !G) { split(el); return; }
      const lanzar = () => {
        const w = split(el);
        G.fromTo(w, { yPercent: 60, opacity: 0, filter: 'blur(12px)' },
          { yPercent: 0, opacity: 1, filter: 'blur(0px)', duration: 0.95, stagger: 0.07, ease: 'expo.out', delay: parseFloat(el.dataset.vlDelay || '0'),
            onComplete: () => G.set(w, { clearProps: 'filter,transform' }) });
      };
      if (el.dataset.vlSplit === 'load') lanzar(); else inView([el], lanzar);
    });

    // entradas y máscaras
    inView($$('[data-vl-reveal],[data-vl-mask],.vl-draw', root), (el) => el.classList.add('is-in'));

    // contadores
    inView($$('[data-vl-count]', root), (el) => contar(el));

    // texto que se ilumina con el scroll
    $$('[data-vl-scrub]', root).forEach((el) => {
      el.classList.add('vl-scrub');
      if (!anim || !G || !ST) { split(el); return; }
      // se prepara un poco antes de llegar (margen de 400px)
      inView([el], () => {
        const w = split(el);
        G.set(w, { opacity: parseFloat(el.dataset.vlScrub) || 0.14 });
        G.to(w, { opacity: 1, stagger: 0.1, ease: 'none', scrollTrigger: { trigger: el, start: 'top 82%', end: 'bottom 42%', scrub: true } });
      }, { rootMargin: '0px 0px 400px 0px', threshold: 0 });
    });

    // parallax
    if (anim && G && ST) $$('[data-vl-parallax]', root).forEach((el) => {
      const v = parseFloat(el.dataset.vlParallax) || -10;
      G.fromTo(el, { yPercent: -v / 2 }, { yPercent: v / 2, ease: 'none', scrollTrigger: { trigger: el.closest('[data-vl-parallax-scope]') || el, start: 'top bottom', end: 'bottom top', scrub: true } });
    });

    // color de fondo de la página por sección
    if (anim && ST && cfg.bgScroll !== false) $$('[data-vl-bg]', root).forEach((el) => {
      ST.create({ trigger: el, start: 'top 55%', end: 'bottom 55%',
        onToggle: (s) => { if (s.isActive) document.body.style.setProperty('--vl-page-bg', el.dataset.vlBg); } });
    });

    // botones: magnetismo + onda al tocar
    $$('[data-vl-magnet]', root).forEach((b) => {
      if (!fino || !anim || !G || b.dataset.vlMagOk) return;
      b.dataset.vlMagOk = '1';
      const qx = G.quickTo(b, 'x', { duration: 0.5, ease: 'power3' }), qy = G.quickTo(b, 'y', { duration: 0.5, ease: 'power3' });
      b.addEventListener('pointermove', (e) => { const r = b.getBoundingClientRect(); qx((e.clientX - r.left - r.width / 2) * 0.22); qy((e.clientY - r.top - r.height / 2) * 0.32); });
      b.addEventListener('pointerleave', () => { qx(0); qy(0); });
    });

    // módulos con lógica propia
    $$('[data-vl-module]', root).forEach((el) => {
      const fn = Velura.modules[el.dataset.vlModule];
      if (fn && !el.dataset.vlModOk) { el.dataset.vlModOk = '1'; try { fn(el, { G, ST, anim, fino, split, contar, inView }); } catch (err) { console.warn('Velura', err); } }
    });
  }
  Velura.init = init;

  // onda al tocar cualquier botón de la marca
  document.addEventListener('pointerdown', (e) => {
    const b = e.target.closest('.vl-btn');
    if (!b) return;
    const r = b.getBoundingClientRect(), s = document.createElement('span'), d = Math.max(r.width, r.height);
    s.className = 'vl-ripple';
    s.style.cssText = `width:${d}px;height:${d}px;left:${e.clientX - r.left - d / 2}px;top:${e.clientY - r.top - d / 2}px`;
    b.append(s); setTimeout(() => s.remove(), 700);
  });

  /* ---------- cabecera ---------- */
  function initHeader() {
    const hdr = document.querySelector('[data-vl-header]');
    const anuncio = document.querySelector('.vl-anuncio');
    const medir = () => H.style.setProperty('--vl-anuncio-h', (anuncio ? anuncio.offsetHeight : 0) + 'px');
    medir(); addEventListener('resize', medir);
    if (!hdr) return;
    let glass = null;
    const tick = () => {
      const g = window.scrollY > 24;
      if (g !== glass) { glass = g; hdr.classList.toggle('is-glass', g); }
    };
    tick(); addEventListener('scroll', tick, { passive: true });

    const btn = hdr.querySelector('[data-vl-menu-btn]'), menu = document.querySelector('[data-vl-menu]');
    if (btn && menu) {
      const set = (open) => {
        menu.classList.toggle('is-open', open); hdr.classList.toggle('is-menu', open);
        H.classList.toggle('vl-menu-abierto', open); btn.setAttribute('aria-expanded', open);
        menu.setAttribute('aria-hidden', !open);
      };
      btn.addEventListener('click', () => set(!menu.classList.contains('is-open')));
      menu.addEventListener('click', (e) => { if (e.target.closest('a')) set(false); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') set(false); });
    }

    // el contador del carrito rebota cuando cambia
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) new MutationObserver(() => {
      const c = bubble.querySelector('.cart-count-bubble');
      if (!c) return; c.classList.remove('vl-bota'); void c.offsetWidth; c.classList.add('vl-bota');
    }).observe(bubble, { childList: true, subtree: true });
  }

  /* ---------- halo del cursor ---------- */
  function initCursor() {
    if (!fino || !anim || cfg.cursor === false || !G) return;
    const c = document.createElement('div');
    c.className = 'vl-cursor'; c.setAttribute('aria-hidden', 'true');
    document.body.append(c);
    const x = G.quickTo(c, 'x', { duration: 0.6, ease: 'power3' }), y = G.quickTo(c, 'y', { duration: 0.6, ease: 'power3' });
    addEventListener('pointermove', (e) => { H.classList.add('vl-cursor-on'); x(e.clientX); y(e.clientY); }, { passive: true });
    document.addEventListener('pointerleave', () => H.classList.remove('vl-cursor-on'));
  }

  /* ---------- coreografía de entrada ---------- */
  function initIntro() {
    const els = $$('[data-vl-intro]');
    if (!els.length) return;
    if (!anim || !G || cfg.intro === false) { G && G.set(els, { opacity: 1 }); return; }
    G.fromTo(els, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.7, stagger: 0.06, delay: 0.3, ease: 'expo.out', clearProps: 'transform' });
  }

  function arrancar() {
    initLenis();
    initHeader();
    init(document);
    initIntro();
    initCursor();
    if (ST) addEventListener('load', () => ST.refresh());
  }
  // se espera a DOMContentLoaded para que vl-secciones.js (también diferido) registre antes sus módulos
  document.readyState === 'complete' ? arrancar() : document.addEventListener('DOMContentLoaded', arrancar);

  /* ---------- editor de Shopify: recargar la sección editada ---------- */
  document.addEventListener('shopify:section:load', (e) => { init(e.target); if (ST) ST.refresh(); });
  document.addEventListener('shopify:section:unload', (e) => {
    if (ST) ST.getAll().forEach((t) => { if (t.trigger && e.target.contains(t.trigger)) t.kill(); });
  });
})();
